// src/main.jsx
import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import HostGuard from "./security/HostGuard.jsx";
import { isOfficialCassavaChainHost } from "./security/officialDomains";
import { consumePreReactEntryIntent, hasBuyerGatewayPublicLandingProtection } from "./auth/entryClassifier";
import { BUYER_GATEWAY_OUTBOUND_HANDOFF_KEY } from "./auth/appEntryIntentResolver";
import { isFreshBuyerGatewayOutboundSignal, isValidBuyerGatewaySettlementSnapshot } from "./auth/buyerGatewayPublicSettlementValidation.js";
import { isAuthCriticalMagicLinkUrl, isMagicLinkEntryUrl } from "./auth/magicLinkEntryDetection.js";
import { shouldEnableRootEntryV2PhysicalStaging } from "./entry-v2/rootEntryV2PhysicalStagingGate.js";

// ✅ PWA: register service worker (auto update) — keep main app behavior,
// but do NOT register it for the lightweight Buyer China Gateway shell.
import { registerSW } from "virtual:pwa-register";

const SW_UPDATE_GUARD_KEY = "cc_sw_update_guard_v4";
const SW_UPDATE_HANDOFF_KEY = "cc_sw_update_handoff_v1";
const AUTH_CRITICAL_SECTION_KEY = "cc_auth_critical_section_v1";
const SW_UPDATE_GUARD_TTL_MS = 5 * 60 * 1000;
const SW_UPDATE_HANDOFF_TTL_MS = 30 * 1000;
const SW_UPDATE_DEFER_POLL_MS = 2500;
const SW_UPDATE_CONTROLLERCHANGE_TIMEOUT_MS = 7000;
const AUTH_CRITICAL_SECTION_TTL_MS = 5 * 60 * 1000;
const SW_UPDATE_RELOAD_ONCE_KEY = "cc_sw_update_reload_once_v1";
const MODULE_IMPORT_RECOVERY_KEY = "cc_module_import_recovery_v1";
const MODULE_IMPORT_TRACE_KEY = "cc_module_import_trace_v1";
const MODULE_IMPORT_RECOVERY_TTL_MS = 2 * 60 * 1000;
const MODULE_IMPORT_AUTO_RELOAD_DELAY_MS = 600;
const MODULE_IMPORT_TRACE_LIMIT = 8;
const PREBOOT_FADE_OUT_MS = 180;
const MAGIC_LINK_DIAGNOSTIC_KEY = "cassava.magicLink.diagnostics.v1";
const MAGIC_LINK_DIAGNOSTIC_LIMIT = 20;
const EXPLICIT_LOGOUT_HANDOFF_KEY = "cc_explicit_logout_handoff_v1";
const BUYER_GATEWAY_RETURN_FUSE_CONSUMED_KEY = "cc_buyer_gateway_return_fuse_consumed_v1";
const BUYER_GATEWAY_RETURN_FUSE_CONSUMED_TTL_MS = 20 * 1000;
const BUYER_GATEWAY_PUBLIC_SETTLEMENT_EVENT = "cassavachain:buyer-gateway-public-settlement";
const BUYER_GATEWAY_RETURN_KEYS = [
  "cc_buyer_gateway_return_intent",
  "cc_force_public_landing_once",
];
const ROOT_ENTRY_MOCK_ENABLED = import.meta.env.VITE_CASSAVACHAIN_ROOT_ENTRY_MOCK === "1";
const ROOT_ENTRY_PRODUCTION_ENABLED = import.meta.env.VITE_CASSAVACHAIN_ROOT_ENTRY_PRODUCTION === "1";
const BOOT_TRACE_HANDLE = "__CC_PWA_BOOT_TRACE__";

let prebootCloseTimer = 0;
let prebootRemoveTimer = 0;
let prebootCommitObserver = null;

function recordBootTrace(type, details = {}) {
  if (typeof window === "undefined") return;
  try { window[BOOT_TRACE_HANDLE]?.record?.(type, details); } catch (_) {}
}

function getBootDomSnapshot() {
  if (typeof document === "undefined") {
    return {
      rootExists: false,
      rootChildCount: null,
      prebootExists: false,
      prebootVisible: false,
    };
  }
  const root = document.getElementById("root");
  const preboot = document.getElementById("cc-preboot-shell");
  const isVisible = (el) => {
    if (!el) return false;
    try {
      const style = window.getComputedStyle(el);
      const rect = el.getBoundingClientRect();
      return style.display !== "none" &&
        style.visibility !== "hidden" &&
        Number(style.opacity || 1) > 0 &&
        rect.width > 0 &&
        rect.height > 0;
    } catch (_) {
      return false;
    }
  };
  return {
    rootExists: !!root,
    rootChildCount: root ? root.children.length : null,
    prebootExists: !!preboot,
    prebootVisible: isVisible(preboot),
  };
}

function renderRootFailsafeShell(reason = "root_empty_after_boot") {
  if (typeof window === "undefined") return false;
  recordBootTrace("root.failsafe_render", { reason, dom: getBootDomSnapshot() });
  try { return window[BOOT_TRACE_HANDLE]?.showFailsafe?.(reason) === true; } catch (_) { return false; }
}

function scheduleRootNonEmptyCheck(reason = "boot-render") {
  if (typeof window === "undefined") return false;
  const dom = getBootDomSnapshot();
  recordBootTrace("root.nonempty_check", { reason, dom });
  try { return !!window[BOOT_TRACE_HANDLE]?.inspectRoot?.(reason); } catch (_) { return false; }
}

function closePrebootShell({ immediate = false } = {}) {
  if (typeof document === "undefined") return;
  const shell = document.getElementById("cc-preboot-shell");
  if (!shell) return;
  recordBootTrace("preboot.close_requested", { immediate, dom: getBootDomSnapshot() });
  if (prebootCloseTimer) {
    window.clearTimeout(prebootCloseTimer);
    prebootCloseTimer = 0;
  }
  if (prebootRemoveTimer) {
    window.clearTimeout(prebootRemoveTimer);
    prebootRemoveTimer = 0;
  }

  const disconnectCommitObserver = () => {
    if (!prebootCommitObserver) return;
    try { prebootCommitObserver.disconnect(); } catch (_) {}
    prebootCommitObserver = null;
  };

  const remove = () => {
    disconnectCommitObserver();
    try { shell.remove(); } catch (_) {}
    recordBootTrace("preboot.removed", { immediate, dom: getBootDomSnapshot() });
    scheduleRootNonEmptyCheck("preboot-removed");
  };

  if (immediate) {
    remove();
    return;
  }

  const beginCloseAfterCommit = () => {
    const root = document.getElementById("root");
    if (!root || root.children.length === 0) return false;
    disconnectCommitObserver();
    try { window[BOOT_TRACE_HANDLE]?.markReactCommitted?.("root_nonempty_before_preboot_close"); } catch (_) {}
    prebootCloseTimer = window.setTimeout(() => {
      prebootCloseTimer = 0;
      try {
        shell.classList.add("cc-preboot-shell--closing");
        shell.setAttribute("aria-hidden", "true");
      } catch (_) {}
      recordBootTrace("preboot.closing", { dom: getBootDomSnapshot() });
      prebootRemoveTimer = window.setTimeout(remove, PREBOOT_FADE_OUT_MS);
    }, 0);
    return true;
  };

  if (beginCloseAfterCommit()) return;
  recordBootTrace("preboot.close_deferred", { reason: "awaiting_react_root_commit", dom: getBootDomSnapshot() });
  try {
    const root = document.getElementById("root");
    if (!root || typeof MutationObserver !== "function") return;
    disconnectCommitObserver();
    prebootCommitObserver = new MutationObserver(() => {
      beginCloseAfterCommit();
    });
    prebootCommitObserver.observe(root, { childList: true });
  } catch (_) {}
}

function hasExplicitLogoutHandoffMarker() {
  try {
    return window.sessionStorage?.getItem?.(EXPLICIT_LOGOUT_HANDOFF_KEY) === "1";
  } catch (_) {
    return false;
  }
}

function readJsonStorage(storage, key) {
  try {
    const raw = storage?.getItem?.(key);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

function writeJsonStorage(storage, key, value) {
  try { storage?.setItem?.(key, JSON.stringify(value)); } catch (_) {}
}

function recordMagicEntryTiming(eventName, details = {}) {
  if (typeof window === "undefined") return;
  const event = String(eventName || "").trim();
  if (!event) return;
  const safeDetails = {};
  ["attemptId", "phase", "status", "reason", "version"].forEach((key) => {
    const value = details?.[key];
    if (value == null || value === "") return;
    safeDetails[key] = String(value).slice(0, 120);
  });
  const payload = {
    event,
    at: Date.now(),
    t: typeof performance !== "undefined" && typeof performance.now === "function" ? Math.round(performance.now()) : 0,
    clientVersion: "2026-05-21-magic-link-entry-shell-phase2b",
    ...safeDetails,
  };
  try { performance?.mark?.(`cc_magic_${event}`); } catch (_) {}
  try {
    const raw = window.sessionStorage?.getItem?.(MAGIC_LINK_DIAGNOSTIC_KEY);
    const list = raw ? JSON.parse(raw) : [];
    const next = Array.isArray(list) ? [...list, payload].slice(-MAGIC_LINK_DIAGNOSTIC_LIMIT) : [payload];
    window.sessionStorage?.setItem?.(MAGIC_LINK_DIAGNOSTIC_KEY, JSON.stringify(next));
  } catch (_) {}
}

function resolveMagicEntryLanguage() {
  try {
    const params = new URL(window.location?.href || "", window.location?.origin || "https://cassavachain.local").searchParams;
    const requested = String(params.get("lang") || "").trim().toLowerCase();
    if (requested === "zh" || requested === "am" || requested === "en") return requested;
  } catch (_) {}
  try {
    const stored = String(window.localStorage?.getItem?.("cassava_lang") || window.localStorage?.getItem?.("cassava_language") || "").trim().toLowerCase();
    if (stored === "zh" || stored === "am" || stored === "en") return stored;
  } catch (_) {}
  try {
    const nav = String(navigator?.language || "").toLowerCase();
    if (nav.startsWith("zh")) return "zh";
    if (nav.startsWith("am")) return "am";
  } catch (_) {}
  return "en";
}

const MAGIC_ENTRY_COPY = Object.freeze({
  en: {
    title: "Completing sign-in",
    body: "Checking your link and preparing CassavaChain.",
    failedTitle: "Sign-in is still available.",
    failedBody: "Return to sign in and request a new link or use Email Code.",
    action: "Return to sign in",
  },
  zh: {
    title: "正在验证安全登录链接...",
    body: "CassavaChain 正在准备安全登录界面。",
    failedTitle: "仍可安全登录。",
    failedBody: "登录界面未能完成加载。请返回登录，重新获取链接或使用邮箱验证码。",
    action: "返回登录",
  },
  am: {
    title: "የደህንነት መግቢያ አገናኝ በማረጋገጥ ላይ...",
    body: "CassavaChain የደህንነት መግቢያ ገጹን እያዘጋጀ ነው።",
    failedTitle: "የደህንነት መግቢያ አሁንም ይገኛል።",
    failedBody: "የመግቢያ ገጹ መጫን አልተጠናቀቀም። ወደ መግቢያ ተመልሰው አዲስ አገናኝ ይጠይቁ ወይም የኢሜይል ኮድ ይጠቀሙ።",
    action: "ወደ መግቢያ ተመለስ",
  },
});

const BOOTSTRAP_RECOVERY_COPY = Object.freeze({
  en: {
    title: "CassavaChain was updated.",
    body: "Reload to continue.",
    action: "Reload CassavaChain",
    waiting: "Preparing a safe reload...",
  },
  zh: {
    title: "CassavaChain 已更新。",
    body: "请重新加载以继续。",
    action: "重新加载 CassavaChain",
    waiting: "正在准备安全重新加载...",
  },
  am: {
    title: "CassavaChain ተዘምኗል።",
    body: "ለመቀጠል እንደገና ይጫኑ።",
    action: "CassavaChainን እንደገና ጫን",
    waiting: "ደህንነቱ የተጠበቀ ዳግም መጫን በመዘጋጀት ላይ...",
  },
});

function escapeHtml(value = "") {
  return String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function isModuleImportFailure(error) {
  const raw = [
    error?.message,
    error?.reason?.message,
    error?.error?.message,
    error?.type,
    error?.name,
  ].filter(Boolean).join(" ").toLowerCase();
  return (
    raw.includes("importing a module script failed") ||
    raw.includes("failed to fetch dynamically imported module") ||
    raw.includes("error loading dynamically imported module") ||
    raw.includes("import()") ||
    raw.includes("modulepreload") ||
    raw.includes("vite:preloaderror") ||
    raw.includes("module script")
  );
}

function getSafeBootstrapLocation() {
  try {
    return {
      host: String(window.location?.host || "").slice(0, 120),
      path: String(window.location?.pathname || "/").slice(0, 180),
    };
  } catch (_) {
    return { host: "", path: "" };
  }
}

function recordModuleImportTrace(kind = "module_import_failure", details = {}) {
  if (typeof window === "undefined") return;
  const location = getSafeBootstrapLocation();
  const payload = {
    kind: String(kind || "module_import_failure").slice(0, 80),
    at: Date.now(),
    authCritical: !!details.authCritical,
    autoReload: !!details.autoReload,
    attemptCount: Number(details.attemptCount || 0),
    host: location.host,
    path: location.path,
    errorName: String(details.errorName || "").slice(0, 80),
  };
  try {
    const raw = window.sessionStorage?.getItem?.(MODULE_IMPORT_TRACE_KEY);
    const list = raw ? JSON.parse(raw) : [];
    const next = Array.isArray(list) ? [...list, payload].slice(-MODULE_IMPORT_TRACE_LIMIT) : [payload];
    window.sessionStorage?.setItem?.(MODULE_IMPORT_TRACE_KEY, JSON.stringify(next));
  } catch (_) {}
  try { console.warn("[CassavaChain Bootstrap]", payload); } catch (_) {}
}

function readModuleImportRecoveryAttempt() {
  const current = readJsonStorage(window.sessionStorage, MODULE_IMPORT_RECOVERY_KEY);
  const at = Number(current?.at || 0);
  if (!current || !at || Date.now() - at > MODULE_IMPORT_RECOVERY_TTL_MS) return null;
  return current;
}

function markModuleImportRecoveryAttempt(kind = "module_import_failure") {
  const previous = readModuleImportRecoveryAttempt();
  const next = {
    kind: String(kind || "module_import_failure").slice(0, 80),
    at: Date.now(),
    count: Number(previous?.count || 0) + 1,
  };
  writeJsonStorage(window.sessionStorage, MODULE_IMPORT_RECOVERY_KEY, next);
  return next;
}

function renderBootstrapRecoveryShell({ kind = "module_import_failure", error = null, autoReload = false } = {}) {
  if (typeof document === "undefined") return;
  recordBootTrace("bootstrap.recovery_shell_render", {
    kind,
    autoReload,
    errorName: error?.name || error?.code || "",
    dom: getBootDomSnapshot(),
  });
  const lang = resolveMagicEntryLanguage();
  const copy = BOOTSTRAP_RECOVERY_COPY[lang] || BOOTSTRAP_RECOVERY_COPY.en;
  const authCritical = isAuthOrRestoreInProgress();
  const attempted = readModuleImportRecoveryAttempt();
  const attemptCount = Number(attempted?.count || 0);
  recordModuleImportTrace(kind, {
    authCritical,
    autoReload,
    attemptCount,
    errorName: error?.name || error?.code || "",
  });

  if (!autoReload && renderRootFailsafeShell(`bootstrap:${kind}`)) return;

  closePrebootShell({ immediate: true });
  const root = document.getElementById("root") || document.body;

  root.innerHTML = `
    <main style="min-height:100vh;display:flex;align-items:center;justify-content:center;padding:max(32px, env(safe-area-inset-top)) 24px max(32px, env(safe-area-inset-bottom));background:#f8fafc;color:#0f172a;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;text-align:center;">
      <section role="alert" aria-live="assertive" style="width:min(92vw,30rem);border:1px solid #e2e8f0;border-radius:24px;background:rgba(255,255,255,.97);padding:38px 26px;box-shadow:0 1px 2px rgba(15,23,42,.05);">
        <img src="/logo_web_64h.png" alt="CassavaChain" style="width:128px;height:auto;margin:0 auto 24px;" />
        <h1 style="margin:0;font-size:clamp(1.55rem,6vw,2rem);line-height:1.14;font-weight:900;">${escapeHtml(copy.title)}</h1>
        <p style="margin:14px auto 0;max-width:22rem;color:#475569;font-size:.98rem;line-height:1.65;">${escapeHtml(autoReload ? copy.waiting : copy.body)}</p>
        <button type="button" data-cc-bootstrap-reload="true" style="margin-top:24px;min-height:46px;border:0;border-radius:14px;background:#0f172a;color:#fff;padding:0 18px;font-size:.92rem;font-weight:800;cursor:pointer;">
          ${escapeHtml(copy.action)}
        </button>
      </section>
    </main>
  `;

  const button = root.querySelector?.("[data-cc-bootstrap-reload='true']");
  if (button) {
    button.addEventListener("click", () => {
      recordModuleImportTrace("module_import_manual_reload", {
        authCritical: isAuthOrRestoreInProgress(),
        autoReload: false,
        attemptCount,
      });
      recordBootTrace("reload.attempt", {
        reason: "module_import_manual_reload",
        authCritical: isAuthOrRestoreInProgress(),
        autoReload: false,
      });
      try { window.location.reload(); } catch (_) {}
    }, { once: true });
  }
  scheduleRootNonEmptyCheck(`bootstrap-recovery:${kind}`);
}

function maybeAutoReloadAfterModuleFailure(kind = "module_import_failure", error = null) {
  const authCritical = isAuthOrRestoreInProgress();
  const previous = readModuleImportRecoveryAttempt();
  if (authCritical || Number(previous?.count || 0) >= 1) {
    renderBootstrapRecoveryShell({ kind, error, autoReload: false });
    return false;
  }
  const attempt = markModuleImportRecoveryAttempt(kind);
  renderBootstrapRecoveryShell({ kind, error, autoReload: true });
  recordModuleImportTrace("module_import_auto_reload_queued", {
    authCritical: false,
    autoReload: true,
    attemptCount: Number(attempt?.count || 0),
    errorName: error?.name || error?.code || "",
  });
  window.setTimeout(() => {
    recordBootTrace("reload.attempt", {
      reason: "module_import_auto_reload",
      kind,
      authCritical: false,
      autoReload: true,
    });
    try { window.location.reload(); } catch (_) {}
  }, MODULE_IMPORT_AUTO_RELOAD_DELAY_MS);
  return true;
}

function MagicLinkImmediateShell({ failed = false }) {
  const lang = resolveMagicEntryLanguage();
  const copy = MAGIC_ENTRY_COPY[lang] || MAGIC_ENTRY_COPY.en;
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "max(32px, env(safe-area-inset-top)) 24px max(32px, env(safe-area-inset-bottom))",
        background: "#f8fafc",
        color: "#0f172a",
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        textAlign: "center",
      }}
    >
      <section
        aria-live="polite"
        aria-busy={failed ? "false" : "true"}
        style={{
          width: "min(92vw, 30rem)",
          border: "1px solid #e2e8f0",
          borderRadius: 30,
          background: "rgba(255,255,255,0.96)",
          padding: "40px 28px",
          boxShadow: "0 1px 2px rgba(15,23,42,0.05)",
        }}
      >
        <img
          src="/logo_web_64h.png"
          alt="CassavaChain"
          style={{ width: 128, height: "auto", margin: "0 auto 24px" }}
        />
        <h1 style={{ margin: 0, fontSize: "clamp(1.55rem, 6vw, 2rem)", lineHeight: 1.12, fontWeight: 900 }}>
          {failed ? copy.failedTitle : copy.title}
        </h1>
        <p style={{ margin: "16px auto 0", maxWidth: "22rem", color: "#475569", fontSize: "0.95rem", lineHeight: 1.7 }}>
          {failed ? copy.failedBody : copy.body}
        </p>
        {failed ? (
          <a
            href="/"
            style={{
              display: "inline-flex",
              marginTop: 24,
              minHeight: 44,
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 16,
              background: "#0f172a",
              color: "#fff",
              padding: "0 18px",
              textDecoration: "none",
              fontSize: "0.9rem",
              fontWeight: 800,
            }}
          >
            {copy.action}
          </a>
        ) : (
          <div
            aria-hidden="true"
            style={{
              width: "min(18rem, 72vw)",
              height: 4,
              margin: "28px auto 0",
              borderRadius: 999,
              background: "linear-gradient(90deg, #0f172a 0%, #64748b 45%, #e2e8f0 100%)",
            }}
          />
        )}
      </section>
    </div>
  );
}

class MagicLinkEntryErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error) {
    recordMagicEntryTiming("magic_entry_render_error", {
      phase: "entry_render",
      status: "error",
      reason: error?.name || "render_error",
    });
  }

  render() {
    if (this.state.failed) return <MagicLinkImmediateShell failed />;
    return this.props.children;
  }
}

function markSwUpdateOnce(reason = "sw-update") {
  if (typeof window === "undefined") return false;
  try {
    const existing = readJsonStorage(window.sessionStorage, SW_UPDATE_GUARD_KEY) || readJsonStorage(window.localStorage, SW_UPDATE_GUARD_KEY);
    const at = Number(existing?.at || 0);
    if (at && Date.now() - at < SW_UPDATE_GUARD_TTL_MS) return false;
    const payload = { reason, at: Date.now() };
    writeJsonStorage(window.sessionStorage, SW_UPDATE_GUARD_KEY, payload);
    writeJsonStorage(window.localStorage, SW_UPDATE_GUARD_KEY, payload);
    return true;
  } catch (_) {
    return false;
  }
}

function markSwUpdateHandoff(reason = "sw-update") {
  if (typeof window === "undefined") return;
  try {
    const payload = {
      reason,
      at: Date.now(),
      expiresAt: Date.now() + SW_UPDATE_HANDOFF_TTL_MS,
      handoffId: `sw_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`,
    };
    writeJsonStorage(window.sessionStorage, SW_UPDATE_HANDOFF_KEY, payload);
    recordBootTrace("sw.handoff.marked", { reason, handoff: true });
  } catch (_) {}
}

function clearSwUpdateHandoff() {
  if (typeof window === "undefined") return;
  try { window.sessionStorage?.removeItem?.(SW_UPDATE_HANDOFF_KEY); } catch (_) {}
  recordBootTrace("sw.handoff.cleared", {});
}

function markSwUpdateReloadOnce(reason = "sw-update-reload") {
  if (typeof window === "undefined") return false;
  try {
    const existing = readJsonStorage(window.sessionStorage, SW_UPDATE_RELOAD_ONCE_KEY);
    const at = Number(existing?.at || 0);
    const expiresAt = Number(existing?.expiresAt || 0);
    if ((expiresAt && Date.now() < expiresAt) || (at && Date.now() - at < SW_UPDATE_HANDOFF_TTL_MS)) return false;
    writeJsonStorage(window.sessionStorage, SW_UPDATE_RELOAD_ONCE_KEY, {
      reason,
      at: Date.now(),
      expiresAt: Date.now() + SW_UPDATE_HANDOFF_TTL_MS,
    });
    return true;
  } catch (_) {
    return false;
  }
}

function logPwaDiagnostic(eventName = "pwa-event", details = {}) {
  if (typeof console === "undefined") return;
  const payload = {
    event: String(eventName || "pwa-event"),
    reason: String(details.reason || ""),
    authOrRestoreInProgress: !!details.authOrRestoreInProgress,
    deferredMs: Number(details.deferredMs || 0),
    handoff: !!details.handoff,
    controllerChange: !!details.controllerChange,
    fallbackReload: !!details.fallbackReload,
  };
  try { console.warn("[CassavaChain PWA]", payload); } catch (_) {}
  recordBootTrace(`pwa.${eventName}`, payload);
}

function isAuthCriticalPath() {
  try {
    const path = String(window.location.pathname || "");
    if (/^\/__\/(auth|firebase)(?:\/|$)/.test(path)) return true;
    if (isAuthCriticalMagicLinkUrl(window.location?.href || "")) return true;
  } catch (_) {}
  return false;
}

function hasBuyerGatewayPublicSettlementContext() {
  try {
    if (hasBuyerGatewayPublicLandingProtection({ href: window.location?.href || "" })) return true;
    const currentEpoch = Number(window.__CC_AUTH_RUNTIME_EPOCH__ || 0);
    const now = Date.now();
    const rawOutboundMarkers = [
      readJsonStorage(window.sessionStorage, BUYER_GATEWAY_OUTBOUND_HANDOFF_KEY),
      readJsonStorage(window.localStorage, BUYER_GATEWAY_OUTBOUND_HANDOFF_KEY),
    ];
    if (rawOutboundMarkers.some((marker) => isFreshBuyerGatewayOutboundSignal({
      marker,
      currentEpoch,
      now,
    }))) return true;
    const settled = readJsonStorage(window.sessionStorage, BUYER_GATEWAY_RETURN_FUSE_CONSUMED_KEY);
    return isValidBuyerGatewaySettlementSnapshot({
      snapshot: settled,
      currentEpoch,
      now,
      ttlMs: BUYER_GATEWAY_RETURN_FUSE_CONSUMED_TTL_MS,
    });
  } catch {
    return false;
  }
}

function isAuthOrRestoreInProgress({ ignoreServiceWorkerHandoff = false } = {}) {
  if (isAuthCriticalPath()) return true;
  try {
    // Buyer Gateway → Main Platform return is a hard public-landing first-paint
    // path. A service-worker update/reload during this handoff can consume the
    // return marker and then fall through to SessionRecoveryShell, producing
    // the Restore progress reset/jitter observed on mobile. Treat it as an
    // auth-critical handoff for update purposes only; this does not alter
    // claims, routePolicy, or workspace permissions.
    if (hasBuyerGatewayPublicSettlementContext()) return true;

    const keys = [
      AUTH_CRITICAL_SECTION_KEY,
      "cc_auth_transition_lock",
      "cc_google_redirect_intent",
      "cc_google_auth_attempt",
      "cc_restore_attempt",
      ...BUYER_GATEWAY_RETURN_KEYS,
    ];
    if (!ignoreServiceWorkerHandoff) keys.push(SW_UPDATE_HANDOFF_KEY);
    return keys.some((key) => {
      const raw = window.sessionStorage?.getItem?.(key) || window.localStorage?.getItem?.(key);
      if (!raw) return false;
      try {
        const parsed = JSON.parse(raw);
        const at = Number(parsed?.startedAt || parsed?.createdAt || parsed?.at || 0);
        const expiresAt = Number(parsed?.expiresAt || 0);
        if (expiresAt && Date.now() > expiresAt) return false;
        return !at || Date.now() - at < AUTH_CRITICAL_SECTION_TTL_MS;
      } catch (_) {
        return true;
      }
    });
  } catch (_) {
    return false;
  }
}

function hasRootEntryProductionAppOwnedMarker() {
  if (typeof window === "undefined") return false;
  const keys = [
    AUTH_CRITICAL_SECTION_KEY,
    SW_UPDATE_HANDOFF_KEY,
    "cassava_explicit_logout_intent",
    "cc_auth_transition_lock",
    "cc_google_redirect_intent",
    "cc_google_auth_attempt",
    "cc_restore_attempt",
    ...BUYER_GATEWAY_RETURN_KEYS,
  ];
  return keys.some((key) => {
    try {
      return !!(window.sessionStorage?.getItem?.(key) || window.localStorage?.getItem?.(key));
    } catch (_) {
      return false;
    }
  });
}

let swUpdateHandoffInFlight = false;
let swUpdateHandoffCancelledForCurrentDocument = false;
let activeSwUpdateHandoffCancellation = null;
let swUpdateHandoffRetryTimer = 0;

function cancelServiceWorkerUpdateHandoffForCurrentDocument(reason = "buyer-public-settlement", { requireBuyerContext = true } = {}) {
  if (swUpdateHandoffCancelledForCurrentDocument) {
    clearSwUpdateHandoff();
    return true;
  }
  if (requireBuyerContext && !hasBuyerGatewayPublicSettlementContext()) return false;

  swUpdateHandoffCancelledForCurrentDocument = true;
  const cancelActiveHandoff = activeSwUpdateHandoffCancellation;
  activeSwUpdateHandoffCancellation = null;
  if (typeof cancelActiveHandoff === "function") cancelActiveHandoff();
  if (swUpdateHandoffRetryTimer) window.clearTimeout(swUpdateHandoffRetryTimer);
  swUpdateHandoffRetryTimer = 0;
  clearSwUpdateHandoff();
  logPwaDiagnostic("service-worker-update-cancelled-for-current-document", {
    reason,
    authOrRestoreInProgress: true,
    handoff: false,
  });
  return true;
}

function cancelServiceWorkerUpdateHandoffForValidBuyerContext(reason = "buyer-public-settlement") {
  if (swUpdateHandoffCancelledForCurrentDocument) {
    clearSwUpdateHandoff();
    return true;
  }
  if (!hasBuyerGatewayPublicSettlementContext()) return false;
  return cancelServiceWorkerUpdateHandoffForCurrentDocument(reason, { requireBuyerContext: false });
}

if (typeof window !== "undefined") {
  try {
    window.addEventListener(BUYER_GATEWAY_PUBLIC_SETTLEMENT_EVENT, () => {
      recordBootTrace("public_owner.settlement_event_received", { source: "window-event" });
      cancelServiceWorkerUpdateHandoffForValidBuyerContext("app-validated-buyer-public-settlement");
    });
  } catch { void 0; }
}

function forceSwUpdateHardReload(reason = "sw-update-hard-reload", details = {}) {
  clearSwUpdateHandoff();
  logPwaDiagnostic("service-worker-update-hard-reload", {
    reason,
    handoff: true,
    fallbackReload: true,
    ...details,
  });
  recordBootTrace("reload.attempt", { reason, source: "service_worker_hard_reload", ...details });
  try { window.location.reload(); } catch (_) {}
}

function startServiceWorkerUpdateHandoff({ reason = "sw-update", updateSW, registration } = {}) {
  if (typeof window === "undefined") return;
  recordBootTrace("sw.handoff.start", {
    reason,
    cancelled: !!swUpdateHandoffCancelledForCurrentDocument,
    hasRegistration: !!registration,
    hasWaiting: !!registration?.waiting,
    hasController: !!navigator.serviceWorker?.controller,
  });
  if (swUpdateHandoffCancelledForCurrentDocument) {
    clearSwUpdateHandoff();
    recordBootTrace("sw.handoff.cancelled", { reason, stage: "already-cancelled" });
    return;
  }
  let suppressReloadForBuyerGatewaySettlement = hasBuyerGatewayPublicSettlementContext();
  if (suppressReloadForBuyerGatewaySettlement) {
    cancelServiceWorkerUpdateHandoffForCurrentDocument(reason, { requireBuyerContext: false });
    logPwaDiagnostic("service-worker-update-handoff-suppressed-buyer-public-settlement", {
      reason,
      authOrRestoreInProgress: true,
      handoff: false,
    });
    recordBootTrace("reload.suppressed", { reason, source: "service_worker_handoff_start", buyerPublicSettlement: true });
    return;
  }
  if (swUpdateHandoffInFlight) {
    logPwaDiagnostic("service-worker-update-handoff-already-running", {
      reason,
      handoff: true,
    });
    if (!isAuthOrRestoreInProgress({ ignoreServiceWorkerHandoff: true })) {
      if (swUpdateHandoffRetryTimer) return;
      swUpdateHandoffRetryTimer = window.setTimeout(() => {
        swUpdateHandoffRetryTimer = 0;
        if (swUpdateHandoffCancelledForCurrentDocument) return;
        if (cancelServiceWorkerUpdateHandoffForValidBuyerContext(`${reason}-already-running-fallback`)) return;
        forceSwUpdateHardReload(`${reason}-already-running-fallback`, { fallbackReload: true });
      }, 750);
    }
    return;
  }
  swUpdateHandoffInFlight = true;
  markSwUpdateHandoff(reason);

  if (!("serviceWorker" in navigator)) {
    clearSwUpdateHandoff();
    swUpdateHandoffInFlight = false;
    if (markSwUpdateReloadOnce(`${reason}-no-service-worker-api`)) {
      recordBootTrace("reload.attempt", { reason: `${reason}-no-service-worker-api`, source: "service_worker_no_api" });
      try { window.location.reload(); } catch (_) {}
    } else {
      forceSwUpdateHardReload(`${reason}-no-service-worker-api-recent-marker`, { fallbackReload: true });
    }
    return;
  }

  let completed = false;
  let fallbackTimer = 0;
  const cleanup = () => {
    try { navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange); } catch (_) {}
    if (fallbackTimer) window.clearTimeout(fallbackTimer);
    fallbackTimer = 0;
    if (swUpdateHandoffRetryTimer) window.clearTimeout(swUpdateHandoffRetryTimer);
    swUpdateHandoffRetryTimer = 0;
    if (activeSwUpdateHandoffCancellation === cancelCurrentHandoff) activeSwUpdateHandoffCancellation = null;
    swUpdateHandoffInFlight = false;
  };
  const cancelCurrentHandoff = () => {
    completed = true;
    cleanup();
  };
  activeSwUpdateHandoffCancellation = cancelCurrentHandoff;
  const forceReloadAfterBlockedGuard = (reloadReason, details = {}) => {
    forceSwUpdateHardReload(reloadReason, {
      authOrRestoreInProgress: false,
      ...details,
    });
  };
  const reloadOnce = (reloadReason, details = {}) => {
    if (swUpdateHandoffCancelledForCurrentDocument) {
      completed = true;
      cleanup();
      clearSwUpdateHandoff();
      recordBootTrace("sw.handoff.cancelled", { reason: reloadReason, stage: "reload-once" });
      return;
    }
    if (completed) return;
    if (suppressReloadForBuyerGatewaySettlement || hasBuyerGatewayPublicSettlementContext()) {
      suppressReloadForBuyerGatewaySettlement = true;
      cancelServiceWorkerUpdateHandoffForCurrentDocument(reloadReason, { requireBuyerContext: false });
      logPwaDiagnostic("service-worker-update-reload-suppressed-buyer-public-settlement", {
        reason: reloadReason,
        authOrRestoreInProgress: true,
        handoff: true,
        ...details,
      });
      recordBootTrace("reload.suppressed", {
        reason: reloadReason,
        source: "service_worker_handoff_reload",
        buyerPublicSettlement: true,
        ...details,
      });
      return;
    }
    if (isAuthOrRestoreInProgress({ ignoreServiceWorkerHandoff: true })) {
      logPwaDiagnostic("service-worker-update-reload-deferred-auth-critical", {
        reason: reloadReason,
        authOrRestoreInProgress: true,
        handoff: true,
        ...details,
      });
      if (fallbackTimer) window.clearTimeout(fallbackTimer);
      fallbackTimer = window.setTimeout(() => {
        reloadOnce(`${reloadReason}-safe-recheck`, details);
      }, SW_UPDATE_DEFER_POLL_MS);
      return;
    }
    completed = true;
    cleanup();
    logPwaDiagnostic("service-worker-update-reload", {
      reason: reloadReason,
      authOrRestoreInProgress: false,
      handoff: true,
      ...details,
    });
    if (markSwUpdateReloadOnce(reloadReason)) {
      clearSwUpdateHandoff();
      recordBootTrace("reload.attempt", { reason: reloadReason, source: "service_worker_handoff", ...details });
      try { window.location.reload(); } catch (_) {}
      return;
    }
    forceReloadAfterBlockedGuard(reloadReason, details);
  };
  const onControllerChange = () => {
    recordBootTrace("sw.handoff.controllerchange", { reason });
    reloadOnce(`${reason}-controllerchange`, { controllerChange: true });
  };

  try { navigator.serviceWorker.addEventListener("controllerchange", onControllerChange, { once: true }); } catch (_) {}
  fallbackTimer = window.setTimeout(() => {
    recordBootTrace("sw.handoff.timeout", { reason });
    reloadOnce(`${reason}-controllerchange-timeout`, { fallbackReload: true });
  }, SW_UPDATE_CONTROLLERCHANGE_TIMEOUT_MS);

  try {
    if (registration?.waiting?.postMessage) registration.waiting.postMessage({ type: "SKIP_WAITING" });
  } catch (_) {}
  try {
    const maybePromise = updateSW?.(true);
    if (maybePromise?.catch) maybePromise.catch(() => {
      reloadOnce(`${reason}-update-call-failed`, { fallbackReload: true });
    });
  } catch (_) {
    reloadOnce(`${reason}-update-call-threw`, { fallbackReload: true });
  }
}

const isBuyerChinaEntry = () => {
  const host = String(window.location.hostname || "").toLowerCase();
  const path = String(window.location.pathname || "");
  return host.startsWith("buyer.") || path === "/buyer-cn" || path.startsWith("/buyer-cn/");
};

async function cleanupBuyerChinaServiceWorker() {
  // Buyer Gateway must not be controlled by an old app-shell service worker.
  // Old cached index.html/assets were proven to keep the Hong Kong entry on a
  // stale bundle after deploy. Keep this cleanup scoped to buyer.cassavachain.com
  // and /buyer-cn so the main CassavaChain PWA remains untouched.
  try {
    if ("serviceWorker" in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
    }
  } catch (error) {
    console.warn("[CassavaChain] Buyer Gateway service worker cleanup skipped", error);
  }

  try {
    if ("caches" in window) {
      const cacheNames = await window.caches.keys();
      await Promise.all(
        cacheNames
          .filter((name) => /workbox|precache|cassava|vite|buyer/i.test(name))
          .map((name) => window.caches.delete(name))
      );
    }
  } catch (error) {
    console.warn("[CassavaChain] Buyer Gateway cache cleanup skipped", error);
  }
}

function registerMainServiceWorker({ bootstrapEntry = null } = {}) {
  if (bootstrapEntry?.forcePublicLanding) {
    // Do not register/update the main PWA service worker during the Buyer
    // Gateway return handoff. This preserves the already validated public
    // landing first paint and prevents deployment/runtime reloads from
    // converting a public return into Workspace Restore.
    recordBootTrace("sw.registration.skipped", { reason: "buyer_gateway_public_landing", forcePublicLanding: true });
    return;
  }

  recordBootTrace("sw.registration.start", { forcePublicLanding: false });
  let updateSW;
  let deferredUpdateTimer = 0;
  let activeRegistration = null;
  const applyDeferredUpdateWhenSafe = (reason = "deferred") => {
    if (deferredUpdateTimer) return;
    const check = () => {
      deferredUpdateTimer = 0;
      if (swUpdateHandoffCancelledForCurrentDocument) {
        clearSwUpdateHandoff();
        return;
      }
      if (cancelServiceWorkerUpdateHandoffForValidBuyerContext(`${reason}-deferred-check`)) return;
      if (isAuthOrRestoreInProgress({ ignoreServiceWorkerHandoff: true })) {
        markSwUpdateHandoff(`${reason}-still-auth-critical`);
        recordBootTrace("sw.update.deferred", { reason: `${reason}-still-auth-critical` });
        deferredUpdateTimer = window.setTimeout(check, SW_UPDATE_DEFER_POLL_MS);
        return;
      }
      if (!markSwUpdateOnce(`${reason}-safe`)) return;
      markSwUpdateHandoff(`${reason}-safe`);
      logPwaDiagnostic("service-worker-update-deferred-apply", {
        reason,
        authOrRestoreInProgress: false,
        deferredMs: SW_UPDATE_DEFER_POLL_MS,
        handoff: true,
      });
      startServiceWorkerUpdateHandoff({ reason: `${reason}-safe`, updateSW, registration: activeRegistration });
    };
    deferredUpdateTimer = window.setTimeout(check, SW_UPDATE_DEFER_POLL_MS);
  };
  updateSW = registerSW({
    immediate: true,
    onNeedRefresh() {
      recordBootTrace("sw.need_refresh", {});
      // PWA/Desktop App must not keep running an obsolete auth shell after a
      // deployment, but Safari must never enter a reload/update storm during
      // OAuth or session restore. Guard the activation to once per short TTL.
      if (cancelServiceWorkerUpdateHandoffForValidBuyerContext("need-refresh-buyer-public-settlement")) {
        logPwaDiagnostic("service-worker-update-suppressed-buyer-public-settlement", {
          reason: "buyer_gateway_public_settlement",
          authOrRestoreInProgress: true,
          handoff: false,
        });
        return;
      }
      if (isAuthOrRestoreInProgress()) {
        markSwUpdateHandoff("need-refresh-deferred-during-restore");
        recordBootTrace("sw.update.deferred", { reason: "auth_or_restore_in_progress" });
        logPwaDiagnostic("service-worker-update-deferred", {
          reason: "auth_or_restore_in_progress",
          authOrRestoreInProgress: true,
          deferredMs: SW_UPDATE_DEFER_POLL_MS,
          handoff: true,
        });
        applyDeferredUpdateWhenSafe("need-refresh-deferred");
        return;
      }
      if (!markSwUpdateOnce("need-refresh")) return;
      markSwUpdateHandoff("need-refresh");
      logPwaDiagnostic("service-worker-update-apply", {
        reason: "need-refresh",
        authOrRestoreInProgress: false,
        handoff: true,
      });
      startServiceWorkerUpdateHandoff({ reason: "need-refresh", updateSW, registration: activeRegistration });
    },
    onRegisteredSW(_swUrl, registration) {
      activeRegistration = registration || null;
      recordBootTrace("sw.registered", {
        scopePresent: !!registration?.scope,
        hasInstalling: !!registration?.installing,
        hasWaiting: !!registration?.waiting,
        hasActive: !!registration?.active,
      });
      try { registration?.update?.(); } catch (_) {}
      if (registration && typeof window !== "undefined") {
        window.setInterval(() => {
          try { registration.update(); } catch (_) {}
        }, 60 * 60 * 1000);
      }
    },
    onRegisterError(error) {
      recordBootTrace("sw.register_error", {
        name: error?.name || "",
        message: error?.message || "",
      });
      console.warn("[CassavaChain] PWA service worker registration failed", error);
    },
  });
}

function handleBootstrapModuleFailure(kind = "module_import_failure", error = null, { force = false } = {}) {
  if (!force && !isModuleImportFailure(error) && kind !== "vite_preload_error") return false;
  maybeAutoReloadAfterModuleFailure(kind, error);
  return true;
}

function installBootstrapModuleFailureHandlers() {
  if (typeof window === "undefined") return;
  window.addEventListener("vite:preloadError", (event) => {
    try { event.preventDefault?.(); } catch (_) {}
    handleBootstrapModuleFailure("vite_preload_error", event?.payload || event?.reason || event?.error || event);
  });
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event?.reason;
    if (!isModuleImportFailure(reason)) return;
    try { event.preventDefault?.(); } catch (_) {}
    handleBootstrapModuleFailure("module_import_unhandled_rejection", reason);
  });
  window.addEventListener("error", (event) => {
    if (!isModuleImportFailure(event)) return;
    try { event.preventDefault?.(); } catch (_) {}
    handleBootstrapModuleFailure("module_script_error", event?.error || event);
  }, true);
}

async function bootstrap() {
  recordBootTrace("bootstrap.start", {
    dom: getBootDomSnapshot(),
    path: String(window.location?.pathname || "/").slice(0, 180),
    searchPresent: !!window.location?.search,
  });
  const rootElement = document.getElementById("root");
  recordBootTrace("bootstrap.root_element", { exists: !!rootElement, dom: getBootDomSnapshot() });
  const root = ReactDOM.createRoot(rootElement);
  const mode = import.meta.env.MODE || "production";
  const hostname = String(window.location.hostname || "");

  if (!isOfficialCassavaChainHost(hostname, mode, { search: window.location?.search || "" })) {
    root.render(
      <React.StrictMode>
        <HostGuard mode={mode} />
      </React.StrictMode>
    );
    closePrebootShell();
    return;
  }

  if (shouldEnableRootEntryV2PhysicalStaging({ env: import.meta.env, location: window.location })) {
    let staging;
    let authLite;
    try {
      [staging, authLite] = await Promise.all([
        import("./entry-v2/RootEntryV2RealStagingHarness.jsx"),
        import("./firebaseAuthLite.js"),
      ]);
    } catch (error) {
      handleBootstrapModuleFailure("root_entry_v2_physical_staging_import_failure", error, { force: true });
      return;
    }

    const RootEntryV2RealStagingHarness = staging.default;
    const auth = authLite.getCassavaAuthLite?.() || null;
    root.render(
      <React.StrictMode>
        <HostGuard mode={mode}>
          <RootEntryV2RealStagingHarness auth={auth} location={window.location} />
        </HostGuard>
      </React.StrictMode>
    );
    closePrebootShell();
    return;
  }

  const buyerChinaEntry = isBuyerChinaEntry();
  const magicLinkEntry = !buyerChinaEntry && isMagicLinkEntryUrl(window.location?.href || "");
  const explicitLogoutHandoffActive = hasExplicitLogoutHandoffMarker();
  const renderMagicLinkEntry = magicLinkEntry ? true : !explicitLogoutHandoffActive && magicLinkEntry;

  if (ROOT_ENTRY_MOCK_ENABLED) {
    let entry;
    let harness;
    let errorBoundary;
    try {
      [entry, harness, errorBoundary] = await Promise.all([
        import("./entry/RootEntryRouter.jsx"),
        import("./entry/rootEntryMockHarness.jsx"),
        import("./entry/RootEntryErrorBoundary.jsx"),
      ]);
    } catch (error) {
      handleBootstrapModuleFailure("root_entry_mock_import_failure", error, { force: true });
      return;
    }

    const RootEntryRouter = entry.default;
    const RootEntryErrorBoundary = errorBoundary.default;
    const rootEntrySearch = window.location?.search || "";
    const rootEntryPathname = window.location?.pathname || "/";
    const renderMockRootEntry = (child, options = {}) => {
      root.render(
        <React.StrictMode>
          <HostGuard mode={mode}>
            <RootEntryErrorBoundary
              forceFallback={!!options.forceFallback}
              fallbackReason={options.fallbackReason || "root_entry_mock"}
            >
              {child}
            </RootEntryErrorBoundary>
          </HostGuard>
        </React.StrictMode>
      );
      closePrebootShell();
    };

    if (harness.shouldUseErrorBoundaryLab?.(rootEntrySearch)) {
      let errorBoundaryLab;
      try {
        errorBoundaryLab = await import("./entry/RootEntryErrorBoundaryLabHarness.jsx");
      } catch {
        renderMockRootEntry(null, {
          forceFallback: true,
          fallbackReason: "error_boundary_lab_import_failure",
        });
        return;
      }

      const RootEntryErrorBoundaryLabHarness = errorBoundaryLab.default;
      renderMockRootEntry(<RootEntryErrorBoundaryLabHarness search={rootEntrySearch} />);
      return;
    }

    if (harness.shouldUseGooglePopupLab?.(rootEntrySearch)) {
      let googlePopupLab;
      try {
        googlePopupLab = await import("./entry/GooglePopupLabHarness.jsx");
      } catch {
        renderMockRootEntry(null, {
          forceFallback: true,
          fallbackReason: "google_popup_lab_import_failure",
        });
        return;
      }

      const GooglePopupLabHarness = googlePopupLab.default;
      renderMockRootEntry(<GooglePopupLabHarness search={rootEntrySearch} />);
      return;
    }

    if (harness.shouldUseGoogleRedirectStartLab?.(rootEntrySearch)) {
      let googleRedirectStartLab;
      try {
        googleRedirectStartLab = await import("./entry/GoogleRedirectStartLabHarness.jsx");
      } catch {
        renderMockRootEntry(null, {
          forceFallback: true,
          fallbackReason: "google_redirect_start_lab_import_failure",
        });
        return;
      }

      const GoogleRedirectStartLabHarness = googleRedirectStartLab.default;
      renderMockRootEntry(<GoogleRedirectStartLabHarness search={rootEntrySearch} />);
      return;
    }

    if (harness.shouldUseGoogleRedirectReturnLab?.(rootEntrySearch)) {
      let googleRedirectReturnLab;
      try {
        googleRedirectReturnLab = await import("./entry/GoogleRedirectReturnLabHarness.jsx");
      } catch {
        renderMockRootEntry(null, {
          forceFallback: true,
          fallbackReason: "google_redirect_return_lab_import_failure",
        });
        return;
      }

      const GoogleRedirectReturnLabHarness = googleRedirectReturnLab.default;
      renderMockRootEntry(<GoogleRedirectReturnLabHarness search={rootEntrySearch} />);
      return;
    }

    if (harness.shouldUsePublicLegalParity?.(rootEntrySearch)) {
      if (harness.shouldRenderPublicLegalParityLegalShell?.({ pathname: rootEntryPathname, search: rootEntrySearch })) {
        let publicLegalParity;
        try {
          publicLegalParity = await import("./entry/RootEntryPublicLegalShell.jsx");
        } catch {
          renderMockRootEntry(null, {
            forceFallback: true,
            fallbackReason: "public_legal_parity_import_failure",
          });
          return;
        }

        const RootEntryPublicLegalShell = publicLegalParity.default;
        renderMockRootEntry(
          <RootEntryPublicLegalShell pathname={rootEntryPathname} search={rootEntrySearch} />
        );
        return;
      }

      let actionDispatcherLab;
      try {
        actionDispatcherLab = await import("./entry/ActionDispatcherLabHarness.jsx");
      } catch {
        renderMockRootEntry(null, {
          forceFallback: true,
          fallbackReason: "public_legal_parity_action_import_failure",
        });
        return;
      }

      const ActionDispatcherLabHarness = actionDispatcherLab.default;
      renderMockRootEntry(<ActionDispatcherLabHarness search={rootEntrySearch} />);
      return;
    }

    if (harness.shouldUseActionDispatcherLab?.(rootEntrySearch)) {
      let actionDispatcherLab;
      try {
        actionDispatcherLab = await import("./entry/ActionDispatcherLabHarness.jsx");
      } catch {
        renderMockRootEntry(null, {
          forceFallback: true,
          fallbackReason: "action_dispatcher_lab_import_failure",
        });
        return;
      }

      const ActionDispatcherLabHarness = actionDispatcherLab.default;
      renderMockRootEntry(<ActionDispatcherLabHarness search={rootEntrySearch} />);
      return;
    }

    if (harness.shouldUseWorkspaceLazyProof?.(rootEntrySearch)) {
      let workspaceLazyProof;
      try {
        workspaceLazyProof = await import("./entry/WorkspaceLazyProofHarness.jsx");
      } catch {
        renderMockRootEntry(null, {
          forceFallback: true,
          fallbackReason: "workspace_lazy_proof_import_failure",
        });
        return;
      }

      const WorkspaceLazyProofHarness = workspaceLazyProof.default;
      renderMockRootEntry(<WorkspaceLazyProofHarness />);
      return;
    }

    if (harness.shouldUseAccessSnapshotAsyncLab?.(rootEntrySearch)) {
      let accessSnapshotLab;
      try {
        accessSnapshotLab = await import("./entry/AccessSnapshotLabHarness.jsx");
      } catch {
        renderMockRootEntry(null, {
          forceFallback: true,
          fallbackReason: "access_snapshot_lab_import_failure",
        });
        return;
      }

      const AccessSnapshotLabHarness = accessSnapshotLab.default;
      renderMockRootEntry(<AccessSnapshotLabHarness search={rootEntrySearch} />);
      return;
    }

    if (harness.shouldUseRootRuntimePreview?.(rootEntrySearch)) {
      let rootRuntimePreview;
      try {
        rootRuntimePreview = await import("./entry/RootEntryRuntimeRouter.jsx");
      } catch {
        renderMockRootEntry(null, {
          forceFallback: true,
          fallbackReason: "root_runtime_preview_import_failure",
        });
        return;
      }

      const RootEntryRuntimeRouter = rootRuntimePreview.default;
      renderMockRootEntry(<RootEntryRuntimeRouter search={rootEntrySearch} />);
      return;
    }

    const mock = harness.createRootEntryMockHarness({ search: rootEntrySearch });
    renderMockRootEntry(
      <RootEntryRouter mode={mock.mode} factsSummary={mock.factsSummary} />
    );
    return;
  }

  if (renderMagicLinkEntry) {
    recordMagicEntryTiming("main_magic_entry_detected", { phase: "pre_app_detection" });
    recordMagicEntryTiming("magic_entry_shell_import_start", { phase: "pre_app_import" });
    root.render(
      <React.StrictMode>
        <MagicLinkImmediateShell />
      </React.StrictMode>
    );
    closePrebootShell();
    try {
      const entry = await import("./auth/MagicLinkEntryShell.jsx");
      recordMagicEntryTiming("magic_entry_shell_import_end", { phase: "pre_app_import", status: "success" });
      const MagicLinkEntryShell = entry.default;
      root.render(
        <React.StrictMode>
          <HostGuard mode={mode}>
            <MagicLinkEntryErrorBoundary>
              <MagicLinkEntryShell
                onFullAppReady={() => registerMainServiceWorker()}
                onModuleImportFailure={(error) => handleBootstrapModuleFailure("magic_link_full_app_import_failure", error, { force: true })}
              />
            </MagicLinkEntryErrorBoundary>
          </HostGuard>
        </React.StrictMode>
      );
      closePrebootShell();
    } catch (error) {
      recordMagicEntryTiming("magic_entry_shell_import_end", {
        phase: "pre_app_import",
        status: "error",
        reason: error?.name || "import_error",
      });
      handleBootstrapModuleFailure("magic_link_entry_shell_import_failure", error, { force: true });
    }
    return;
  }

  const loadFullApp = async ({ bootstrapEntry = null, moduleFailureKind = "app_module_import_failure" } = {}) => {
    let entry;
    try {
      recordBootTrace("bootstrap.app_import_start", {
        moduleFailureKind,
        forcePublicLanding: !!bootstrapEntry?.forcePublicLanding,
      });
      entry = await import("./App.jsx");
      recordBootTrace("bootstrap.app_import_end", {
        status: "success",
        forcePublicLanding: !!bootstrapEntry?.forcePublicLanding,
      });
    } catch (error) {
      recordBootTrace("bootstrap.app_import_end", {
        status: "error",
        moduleFailureKind,
        errorName: error?.name || "",
      });
      handleBootstrapModuleFailure(moduleFailureKind, error, { force: true });
      return false;
    }

    registerMainServiceWorker({ bootstrapEntry });
    const AppComponent = entry.default;
    recordBootTrace("react.render.requested", {
      branch: "full_app_delegate",
      forcePublicLanding: !!bootstrapEntry?.forcePublicLanding,
    });
    root.render(
      <React.StrictMode>
        <HostGuard mode={mode}>
          <AppComponent bootstrapEntry={bootstrapEntry} />
        </HostGuard>
      </React.StrictMode>
    );
    recordBootTrace("react.render.completed", { branch: "full_app_delegate", dom: getBootDomSnapshot() });
    closePrebootShell();
    scheduleRootNonEmptyCheck("full-app-delegate-render");
    return true;
  };

  if (ROOT_ENTRY_PRODUCTION_ENABLED && !buyerChinaEntry) {
    const rootEntryPathname = window.location?.pathname || "/";
    const rootEntrySearch = window.location?.search || "";
    let productionSwitch;
    try {
      productionSwitch = await import("./entry/RootEntryProductionSwitch.jsx");
    } catch (error) {
      handleBootstrapModuleFailure("root_entry_production_switch_import_failure", error, { force: true });
      return;
    }

    if (
      productionSwitch.isRootEntryProductionSafePath?.({ pathname: rootEntryPathname, search: rootEntrySearch }) &&
      !hasRootEntryProductionAppOwnedMarker()
    ) {
      const RootEntryProductionSwitch = productionSwitch.default;
      const delegateToApp = () => {
        const bootstrapEntry = consumePreReactEntryIntent();
        loadFullApp({ bootstrapEntry });
      };
      root.render(
        <React.StrictMode>
          <HostGuard mode={mode}>
            <RootEntryProductionSwitch
              pathname={rootEntryPathname}
              search={rootEntrySearch}
              onDelegateToApp={delegateToApp}
            />
          </HostGuard>
        </React.StrictMode>
      );
      closePrebootShell();
      return;
    }
  }

  // Buyer Gateway → Main Platform return is a public-landing intent.
  // Consume it before the React app is even imported/rendered so Restore can
  // never win the first paint.
  const bootstrapEntry = buyerChinaEntry ? null : consumePreReactEntryIntent();

  let entry;
  try {
    recordBootTrace("bootstrap.app_import_start", {
      branch: buyerChinaEntry ? "buyer_gateway" : "main_app",
      forcePublicLanding: !!bootstrapEntry?.forcePublicLanding,
    });
    entry = buyerChinaEntry
      ? await import("./buyer-cn/main.jsx")
      : await import("./App.jsx");
    recordBootTrace("bootstrap.app_import_end", {
      branch: buyerChinaEntry ? "buyer_gateway" : "main_app",
      status: "success",
      forcePublicLanding: !!bootstrapEntry?.forcePublicLanding,
    });
  } catch (error) {
    recordBootTrace("bootstrap.app_import_end", {
      branch: buyerChinaEntry ? "buyer_gateway" : "main_app",
      status: "error",
      errorName: error?.name || "",
    });
    handleBootstrapModuleFailure(
      buyerChinaEntry ? "buyer_gateway_module_import_failure" : "app_module_import_failure",
      error,
      { force: true }
    );
    return;
  }

  if (buyerChinaEntry) {
    cleanupBuyerChinaServiceWorker();
  } else {
    registerMainServiceWorker({ bootstrapEntry });
  }

  const AppComponent = entry.default;
  recordBootTrace("react.render.requested", {
    branch: buyerChinaEntry ? "buyer_gateway" : "main_app",
    forcePublicLanding: !!bootstrapEntry?.forcePublicLanding,
  });
  root.render(
    <React.StrictMode>
      <HostGuard mode={mode}>
        <AppComponent bootstrapEntry={bootstrapEntry} />
      </HostGuard>
    </React.StrictMode>
  );
  recordBootTrace("react.render.completed", {
    branch: buyerChinaEntry ? "buyer_gateway" : "main_app",
    dom: getBootDomSnapshot(),
  });
  closePrebootShell();
  scheduleRootNonEmptyCheck(buyerChinaEntry ? "buyer-gateway-render" : "main-app-render");
}

try { window[BOOT_TRACE_HANDLE]?.markMainStarted?.({ path: String(window.location?.pathname || "/").slice(0, 180) }); } catch (_) {}
installBootstrapModuleFailureHandlers();

bootstrap().catch((error) => {
  if (handleBootstrapModuleFailure("bootstrap_unhandled_import_failure", error)) return;
  renderBootstrapRecoveryShell({ kind: "bootstrap_unhandled_failure", error, autoReload: false });
});
